import React, { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  X,
  Search,
  Shield,
  Mail,
  Phone,
  Loader2,
  Check,
  AlertCircle,
  Download,
  Bot,
  MessageSquare,
  Lock,
  Unlock,
  Key,
  Clock,
  Globe,
  Building2,
  MapPin,
  UserCircle,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  Calendar,
  LogIn
} from 'lucide-react';
import { getUsers, createUser, updateUser, deleteUser, getDepartments } from '../services/api';
import api from '../services/api';

const ROLES = [
  { value: 'superadmin', label: 'Super Admin', color: 'bg-purple-100 text-purple-800' },
  { value: 'admin', label: 'Administrador', color: 'bg-blue-100 text-blue-800' },
  { value: 'revisor', label: 'Revisor', color: 'bg-green-100 text-green-800' },
  { value: 'usuario', label: 'Usuario', color: 'bg-gray-100 text-gray-800' }
];

const USER_TYPES = [
  { value: 'team', label: 'Equipo Manantial', icon: Users, color: 'bg-blue-100 text-blue-800', description: 'Empleado interno' },
  { value: 'franchisee', label: 'Franquiciado', icon: Building2, color: 'bg-green-100 text-green-800', description: 'Socio franquiciado' },
  { value: 'lugar', label: 'Lugar', icon: MapPin, color: 'bg-orange-100 text-orange-800', description: 'Usuario de taquilla/lugar' },
  { value: 'client', label: 'Cliente final', icon: UserCircle, color: 'bg-yellow-100 text-yellow-800', description: 'Cliente externo' }
];

const PASSWORD_EXPIRY_OPTIONS = [
  { value: 0, label: 'Nunca expira' },
  { value: 30, label: 'Cada 30 dias' },
  { value: 60, label: 'Cada 60 dias' },
  { value: 90, label: 'Cada 90 dias' },
  { value: 180, label: 'Cada 6 meses' },
  { value: 365, label: 'Cada año' }
];

export default function UserManagement({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    name: '',
    role: 'usuario',
    user_type: 'team',
    phone: '',
    department_ids: [],
    can_download: true,
    can_feed_ai: false,
    can_upload_without_review: false,
    associated_chat_id: '',
    associated_chat_url: '',
    two_factor_enabled: false,
    password_expiry_days: 0,
    force_password_change: false
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [actionModal, setActionModal] = useState(null); // { type: 'block' | 'reset_password' | 'view', user: {...} }
  const [franquiciados, setFranquiciados] = useState([]);
  const [lugares, setLugares] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [copiedUrl, setCopiedUrl] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, deptsRes, franqRes, lugaresRes, clientesRes] = await Promise.all([
        getUsers(),
        getDepartments(),
        api.get('/api/franquiciados').catch(() => ({ data: { franquiciados: [] } })),
        api.get('/api/lugares').catch(() => ({ data: { lugares: [] } })),
        api.get('/api/clientes').catch(() => ({ data: { clientes: [] } }))
      ]);
      setUsers(usersRes.data.users || usersRes.data || []);
      setDepartments(deptsRes.data.departments || deptsRes.data || []);
      setFranquiciados(franqRes.data.franquiciados || []);
      setLugares(lugaresRes.data.lugares || []);
      setClientes(clientesRes.data.clientes || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      if (editingUser) {
        await updateUser(editingUser.id, formData);
      } else {
        await createUser(formData);
      }
      await loadData();
      closeModal();
    } catch (error) {
      setFormError(error.response?.data?.error || 'Error al guardar usuario');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await deleteUser(userId);
      await loadData();
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      username: '',
      name: '',
      role: 'usuario',
      user_type: 'team',
      phone: '',
      department_ids: [],
      can_download: true,
      can_feed_ai: false,
      can_upload_without_review: false,
      associated_chat_id: '',
      associated_chat_url: '',
      two_factor_enabled: false,
      password_expiry_days: 0,
      force_password_change: false
    });
    setFormError('');
    setShowPassword(false);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      username: user.username,
      name: user.name,
      role: user.role,
      user_type: user.user_type || 'team',
      phone: user.phone || '',
      department_ids: user.user_departments?.map(ud => ud.department_id) || [],
      can_download: user.can_download !== false,
      can_feed_ai: user.can_feed_ai || false,
      can_upload_without_review: user.can_upload_without_review || false,
      associated_chat_id: user.associated_chat_id || '',
      associated_chat_url: user.associated_chat_url || '',
      two_factor_enabled: user.two_factor_enabled || false,
      password_expiry_days: user.password_expiry_days || 0,
      force_password_change: user.force_password_change || false
    });
    setFormError('');
    setShowPassword(false);
    setShowModal(true);
  };

  const handleBlockUser = async (user) => {
    try {
      await api.put(`/api/users/${user.id}/block`);
      await loadData();
      setActionModal(null);
    } catch (error) {
      console.error('Error blocking user:', error);
      alert(error.response?.data?.error || 'Error al bloquear usuario');
    }
  };

  const handleResetPassword = async (user, newPassword) => {
    try {
      await api.put(`/api/users/${user.id}/reset-password`, { password: newPassword });
      await loadData();
      setActionModal(null);
      alert('Contraseña actualizada correctamente');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(error.response?.data?.error || 'Error al cambiar contraseña');
    }
  };

  const handleForcePasswordChange = async (user) => {
    try {
      await api.put(`/api/users/${user.id}/force-password-change`);
      await loadData();
      alert('El usuario debera cambiar su contraseña en el proximo inicio de sesion');
    } catch (error) {
      console.error('Error forcing password change:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const getAssociatedChatOptions = () => {
    switch (formData.user_type) {
      case 'franchisee':
        return franquiciados.map(f => ({ id: f.id, name: f.name, url: f.chat_url }));
      case 'lugar':
        return lugares.map(l => ({ id: l.id, name: l.name, url: l.chat_url }));
      case 'client':
        return clientes.map(c => ({ id: c.id, name: c.name, url: c.chat_url }));
      default:
        return [];
    }
  };

  const getUserTypeInfo = (type) => {
    return USER_TYPES.find(t => t.value === type) || USER_TYPES[0];
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormError('');
  };

  const toggleDepartment = (deptId) => {
    setFormData(prev => ({
      ...prev,
      department_ids: prev.department_ids.includes(deptId)
        ? prev.department_ids.filter(id => id !== deptId)
        : [...prev.department_ids, deptId]
    }));
  };

  const getRoleInfo = (role) => {
    return ROLES.find(r => r.value === role) || ROLES[3];
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canManageUser = (targetUser) => {
    if (currentUser?.role === 'superadmin') return true;
    if (currentUser?.role === 'admin') {
      return !['superadmin', 'admin'].includes(targetUser.role);
    }
    return false;
  };

  const getUserDepartments = (user) => {
    return user.user_departments?.map(ud =>
      departments.find(d => d.id === ud.department_id)?.name
    ).filter(Boolean).join(', ') || 'Sin departamentos';
  };

  // Filtrar roles disponibles según el usuario actual
  const getAvailableRoles = () => {
    if (currentUser?.role === 'superadmin') {
      // SuperAdmin puede crear cualquier rol
      return ROLES;
    } else if (currentUser?.role === 'admin') {
      // Admin NO puede crear SuperAdmin
      return ROLES.filter(role => role.value !== 'superadmin');
    }
    // Otros usuarios no deberían llegar aquí, pero por seguridad retornamos solo usuario
    return ROLES.filter(role => role.value === 'usuario');
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Users className="w-6 h-6 text-blue-600" />
          <h2 className="text-lg font-semibold">Gestion de Usuarios</h2>
          <span className="bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full">
            {users.length} usuarios
          </span>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Usuario
        </button>
      </div>

      {/* Search */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar usuarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* User List */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <Users className="w-16 h-16 mb-4 opacity-50" />
            <p>No se encontraron usuarios</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredUsers.map(user => {
              const roleInfo = getRoleInfo(user.role);
              const userTypeInfo = getUserTypeInfo(user.user_type);
              const UserTypeIcon = userTypeInfo.icon;
              const canManage = canManageUser(user);

              return (
                <div
                  key={user.id}
                  className={`bg-white rounded-lg border p-4 hover:shadow-md transition ${!user.is_active ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                        user.is_blocked ? 'bg-red-500' : 'bg-blue-500'
                      }`}>
                        {user.name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-gray-900">{user.name}</h3>
                          {user.is_blocked && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full flex items-center">
                              <Lock className="w-3 h-3 mr-1" />
                              Bloqueado
                            </span>
                          )}
                          {user.two_factor_enabled && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center">
                              <ShieldCheck className="w-3 h-3 mr-1" />
                              2FA
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-3 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Mail className="w-3 h-3 mr-1" />
                            {user.email}
                          </span>
                          {user.phone && (
                            <span className="flex items-center">
                              <Phone className="w-3 h-3 mr-1" />
                              {user.phone}
                            </span>
                          )}
                        </div>
                        {user.last_login && (
                          <div className="text-xs text-gray-400 mt-1 flex items-center">
                            <LogIn className="w-3 h-3 mr-1" />
                            Ultimo acceso: {new Date(user.last_login).toLocaleString('es-ES')}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* User Type Badge */}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${userTypeInfo.color}`}>
                        <UserTypeIcon className="w-3 h-3 inline mr-1" />
                        {userTypeInfo.label}
                      </span>
                      {/* Role Badge */}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleInfo.color}`}>
                        <Shield className="w-3 h-3 inline mr-1" />
                        {roleInfo.label}
                      </span>
                      {/* Status Badge */}
                      <span className={`px-2 py-1 rounded text-xs ${user.is_active && !user.is_blocked ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {user.is_blocked ? 'Bloqueado' : user.is_active ? 'Activo' : 'Inactivo'}
                      </span>

                      {canManage && (
                        <div className="flex items-center space-x-1 ml-2">
                          {/* View Details */}
                          <button
                            onClick={() => setActionModal({ type: 'view', user })}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-blue-600 transition"
                            title="Ver detalles"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {/* Edit */}
                          <button
                            onClick={() => openEditModal(user)}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-blue-600 transition"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {/* Block/Unblock */}
                          <button
                            onClick={() => setActionModal({ type: 'block', user })}
                            className={`p-2 hover:bg-gray-100 rounded-lg transition ${user.is_blocked ? 'text-green-600' : 'text-orange-600'}`}
                            title={user.is_blocked ? 'Desbloquear' : 'Bloquear'}
                          >
                            {user.is_blocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                          </button>
                          {/* Reset Password */}
                          <button
                            onClick={() => setActionModal({ type: 'reset_password', user })}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-purple-600 transition"
                            title="Cambiar contraseña"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          {/* Delete */}
                          <button
                            onClick={() => setDeleteConfirm(user)}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-red-600 transition"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Info row */}
                  <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-3 text-xs">
                    <span className="flex items-center text-gray-600">
                      <MessageSquare className="w-3 h-3 mr-1" />
                      Chats: <span className="ml-1 font-medium">{getUserDepartments(user)}</span>
                    </span>
                    {user.associated_chat_url && (
                      <span className="flex items-center text-blue-600">
                        <Globe className="w-3 h-3 mr-1" />
                        <a href={user.associated_chat_url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-48">
                          {user.associated_chat_url}
                        </a>
                      </span>
                    )}
                    <span className={`flex items-center ${user.can_download !== false ? 'text-green-600' : 'text-gray-400'}`}>
                      <Download className="w-3 h-3 mr-1" />
                      {user.can_download !== false ? 'Descarga' : 'No descarga'}
                    </span>
                    <span className={`flex items-center ${user.can_feed_ai ? 'text-purple-600' : 'text-gray-400'}`}>
                      <Bot className="w-3 h-3 mr-1" />
                      {user.can_feed_ai ? 'Alimenta IA' : 'No IA'}
                    </span>
                    {user.can_upload_without_review && (
                      <span className="flex items-center text-green-600">
                        <Check className="w-3 h-3 mr-1" />
                        Sin revision
                      </span>
                    )}
                    {user.password_expiry_days > 0 && (
                      <span className="flex items-center text-orange-600">
                        <Clock className="w-3 h-3 mr-1" />
                        Expira cada {user.password_expiry_days}d
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-semibold">
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h3>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {formError && (
                <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre completo
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefono
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Tipo de usuario y Rol */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de usuario
                  </label>
                  <select
                    value={formData.user_type}
                    onChange={(e) => setFormData({ ...formData, user_type: e.target.value, associated_chat_id: '', associated_chat_url: '' })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {USER_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {USER_TYPES.find(t => t.value === formData.user_type)?.description}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rol
                    {currentUser?.role === 'admin' && (
                      <span className="ml-2 text-xs text-gray-500">(No puedes crear SuperAdmin)</span>
                    )}
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {getAvailableRoles().map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Chat asociado (para franquiciados, lugares, clientes) */}
              {formData.user_type !== 'team' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Globe className="w-4 h-4 inline mr-1" />
                    Chat asociado
                  </label>
                  <select
                    value={formData.associated_chat_id}
                    onChange={(e) => {
                      const selected = getAssociatedChatOptions().find(o => o.id === e.target.value);
                      setFormData({
                        ...formData,
                        associated_chat_id: e.target.value,
                        associated_chat_url: selected?.url || ''
                      });
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecciona un chat</option>
                    {getAssociatedChatOptions().map(option => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                  {formData.associated_chat_url && (
                    <div className="mt-2 flex items-center p-2 bg-gray-100 rounded-lg">
                      <code className="text-sm text-gray-600 flex-1 truncate">{formData.associated_chat_url}</code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(formData.associated_chat_url)}
                        className="ml-2 p-1 hover:bg-gray-200 rounded"
                      >
                        {copiedUrl ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Password */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password {editingUser && '(vacio = no cambiar)'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 pr-10"
                      required={!editingUser}
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Expiracion contraseña
                  </label>
                  <select
                    value={formData.password_expiry_days}
                    onChange={(e) => setFormData({ ...formData, password_expiry_days: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {PASSWORD_EXPIRY_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Department Access */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  Acceso a Chats/Departamentos
                </label>
                <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
                  {departments.map(dept => (
                    <label
                      key={dept.id}
                      className={`flex items-center p-2 rounded cursor-pointer transition ${
                        formData.department_ids.includes(dept.id)
                          ? 'bg-blue-100 text-blue-800'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.department_ids.includes(dept.id)}
                        onChange={() => toggleDepartment(dept.id)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded border mr-2 flex items-center justify-center ${
                        formData.department_ids.includes(dept.id)
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300'
                      }`}>
                        {formData.department_ids.includes(dept.id) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="text-sm">{dept.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Permisos
                </label>
                <div className="space-y-2">
                  <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
                    <input
                      type="checkbox"
                      checked={formData.can_download}
                      onChange={(e) => setFormData({ ...formData, can_download: e.target.checked })}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border mr-3 flex items-center justify-center ${
                      formData.can_download ? 'bg-green-600 border-green-600' : 'border-gray-300'
                    }`}>
                      {formData.can_download && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <Download className="w-4 h-4 mr-2 text-gray-600" />
                    <span className="text-sm">Puede descargar archivos del chat</span>
                  </label>

                  <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
                    <input
                      type="checkbox"
                      checked={formData.can_feed_ai}
                      onChange={(e) => setFormData({ ...formData, can_feed_ai: e.target.checked })}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border mr-3 flex items-center justify-center ${
                      formData.can_feed_ai ? 'bg-purple-600 border-purple-600' : 'border-gray-300'
                    }`}>
                      {formData.can_feed_ai && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <Bot className="w-4 h-4 mr-2 text-gray-600" />
                    <span className="text-sm">Puede alimentar informacion a la IA</span>
                  </label>

                  <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
                    <input
                      type="checkbox"
                      checked={formData.can_upload_without_review}
                      onChange={(e) => setFormData({ ...formData, can_upload_without_review: e.target.checked })}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border mr-3 flex items-center justify-center ${
                      formData.can_upload_without_review ? 'bg-orange-600 border-orange-600' : 'border-gray-300'
                    }`}>
                      {formData.can_upload_without_review && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <RefreshCw className="w-4 h-4 mr-2 text-gray-600" />
                    <span className="text-sm">Subir contenido sin revision</span>
                  </label>
                </div>
              </div>

              {/* Security */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Shield className="w-4 h-4 inline mr-1" />
                  Seguridad
                </label>
                <div className="space-y-2">
                  <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
                    <input
                      type="checkbox"
                      checked={formData.two_factor_enabled}
                      onChange={(e) => setFormData({ ...formData, two_factor_enabled: e.target.checked })}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border mr-3 flex items-center justify-center ${
                      formData.two_factor_enabled ? 'bg-green-600 border-green-600' : 'border-gray-300'
                    }`}>
                      {formData.two_factor_enabled && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <ShieldCheck className="w-4 h-4 mr-2 text-gray-600" />
                    <div>
                      <span className="text-sm">Activar doble factor (2FA)</span>
                      <p className="text-xs text-gray-500">El usuario recibira un codigo por email al iniciar sesion</p>
                    </div>
                  </label>

                  <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
                    <input
                      type="checkbox"
                      checked={formData.force_password_change}
                      onChange={(e) => setFormData({ ...formData, force_password_change: e.target.checked })}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border mr-3 flex items-center justify-center ${
                      formData.force_password_change ? 'bg-red-600 border-red-600' : 'border-gray-300'
                    }`}>
                      {formData.force_password_change && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <Key className="w-4 h-4 mr-2 text-gray-600" />
                    <div>
                      <span className="text-sm">Forzar cambio de contraseña</span>
                      <p className="text-xs text-gray-500">El usuario debera cambiar su contraseña en el proximo login</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center"
                >
                  {formLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {editingUser ? 'Guardar' : 'Crear'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Eliminar Usuario</h3>
              <p className="text-gray-500 mb-6">
                Estas seguro de que quieres eliminar a <strong>{deleteConfirm.name}</strong>?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block/Unblock Modal */}
      {actionModal?.type === 'block' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                actionModal.user.is_blocked ? 'bg-green-100' : 'bg-orange-100'
              }`}>
                {actionModal.user.is_blocked ? (
                  <Unlock className="w-6 h-6 text-green-600" />
                ) : (
                  <Lock className="w-6 h-6 text-orange-600" />
                )}
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {actionModal.user.is_blocked ? 'Desbloquear' : 'Bloquear'} Usuario
              </h3>
              <p className="text-gray-500 mb-6">
                {actionModal.user.is_blocked
                  ? `¿Desbloquear a ${actionModal.user.name}? Podra volver a acceder al sistema.`
                  : `¿Bloquear a ${actionModal.user.name}? No podra acceder al sistema hasta que lo desbloquees.`
                }
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setActionModal(null)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleBlockUser(actionModal.user)}
                  className={`flex-1 px-4 py-2 text-white rounded-lg transition ${
                    actionModal.user.is_blocked ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  {actionModal.user.is_blocked ? 'Desbloquear' : 'Bloquear'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {actionModal?.type === 'reset_password' && (
        <ResetPasswordModal
          user={actionModal.user}
          onClose={() => setActionModal(null)}
          onSubmit={handleResetPassword}
        />
      )}

      {/* View User Modal */}
      {actionModal?.type === 'view' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center">
                <Eye className="w-5 h-5 mr-2 text-blue-600" />
                Detalles del Usuario
              </h3>
              <button onClick={() => setActionModal(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* User Header */}
              <div className="flex items-center space-x-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl ${
                  actionModal.user.is_blocked ? 'bg-red-500' : 'bg-blue-500'
                }`}>
                  {actionModal.user.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <h4 className="text-xl font-bold">{actionModal.user.name}</h4>
                  <p className="text-gray-500">@{actionModal.user.username}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getRoleInfo(actionModal.user.role).color}`}>
                      {getRoleInfo(actionModal.user.role).label}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getUserTypeInfo(actionModal.user.user_type).color}`}>
                      {getUserTypeInfo(actionModal.user.user_type).label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center text-sm">
                  <Mail className="w-4 h-4 mr-2 text-gray-400" />
                  <span>{actionModal.user.email}</span>
                </div>
                {actionModal.user.phone && (
                  <div className="flex items-center text-sm">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    <span>{actionModal.user.phone}</span>
                  </div>
                )}
                {actionModal.user.associated_chat_url && (
                  <div className="flex items-center text-sm">
                    <Globe className="w-4 h-4 mr-2 text-gray-400" />
                    <a href={actionModal.user.associated_chat_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {actionModal.user.associated_chat_url}
                    </a>
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Estado</p>
                  <p className={`font-medium ${actionModal.user.is_blocked ? 'text-red-600' : actionModal.user.is_active ? 'text-green-600' : 'text-gray-600'}`}>
                    {actionModal.user.is_blocked ? 'Bloqueado' : actionModal.user.is_active ? 'Activo' : 'Inactivo'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">2FA</p>
                  <p className={`font-medium ${actionModal.user.two_factor_enabled ? 'text-green-600' : 'text-gray-400'}`}>
                    {actionModal.user.two_factor_enabled ? 'Activado' : 'Desactivado'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Ultimo acceso</p>
                  <p className="font-medium text-sm">
                    {actionModal.user.last_login
                      ? new Date(actionModal.user.last_login).toLocaleString('es-ES')
                      : 'Nunca'
                    }
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Expiracion password</p>
                  <p className="font-medium text-sm">
                    {actionModal.user.password_expiry_days > 0
                      ? `Cada ${actionModal.user.password_expiry_days} dias`
                      : 'Nunca'
                    }
                  </p>
                </div>
              </div>

              {/* Permissions */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Permisos</p>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2 py-1 rounded text-xs ${actionModal.user.can_download !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    <Download className="w-3 h-3 inline mr-1" />
                    {actionModal.user.can_download !== false ? 'Puede descargar' : 'No descarga'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${actionModal.user.can_feed_ai ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'}`}>
                    <Bot className="w-3 h-3 inline mr-1" />
                    {actionModal.user.can_feed_ai ? 'Alimenta IA' : 'No alimenta IA'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${actionModal.user.can_upload_without_review ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`}>
                    <RefreshCw className="w-3 h-3 inline mr-1" />
                    {actionModal.user.can_upload_without_review ? 'Sin revision' : 'Con revision'}
                  </span>
                </div>
              </div>

              {/* Departments */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Acceso a chats</p>
                <p className="text-sm text-gray-600">{getUserDepartments(actionModal.user)}</p>
              </div>

              {/* Dates */}
              <div className="text-xs text-gray-400 pt-4 border-t">
                <p>Creado: {new Date(actionModal.user.created_at).toLocaleString('es-ES')}</p>
                {actionModal.user.updated_at && (
                  <p>Actualizado: {new Date(actionModal.user.updated_at).toLocaleString('es-ES')}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end p-4 border-t">
              <button
                onClick={() => setActionModal(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Component for Reset Password Modal
function ResetPasswordModal({ user, onClose, onSubmit }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    onSubmit(user, newPassword);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Key className="w-6 h-6 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold">Cambiar Contraseña</h3>
          <p className="text-gray-500 text-sm mt-1">
            Nueva contraseña para <strong>{user.name}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center">
              <AlertCircle className="w-4 h-4 mr-2" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nueva contraseña</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 pr-10"
                minLength={6}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar contraseña</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              Cambiar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
